import { RoundedBadge } from '@/components/rounded-badge';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { Quest } from '@/types';
import { getIconPathFromBadgeId } from '@/utils';
import { Card, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';
import { toWords } from 'number-to-words';
import { useEffect, useMemo } from 'react';
import { IoCheckmark } from 'react-icons/io5';
import { useLocalStorage } from 'usehooks-ts';

export const QuestRewardCard = ({ quest }: { quest: Quest }) => {
  const questIsComplete = useMemo(() => quest?.tasks?.every((task) => task.completed), [quest]);

  const [loggedQuests, setLoggedQuests] = useLocalStorage<Record<string, boolean>>('loggedQuests', {});

  useEffect(() => {
    // Check if we've logged this from local storage
    if (!loggedQuests[quest.id] && questIsComplete) {
      trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCompletedQuest, {
        quest_name: quest.title,
      });
      setLoggedQuests({ ...loggedQuests, [quest.id]: true });
    }
  }, [questIsComplete, quest.id, loggedQuests, setLoggedQuests]);

  return (
    <Card.Root
      bgColor={questIsComplete ? 'brandBlue' : 'bg.muted'}
      borderColor="border.muted"
      p={8}
      borderRadius="3xl"
      w="100%"
    >
      <Card.Body>
        <VStack alignItems={'center'} gap={4}>
          {questIsComplete ? (
            <Icon boxSize={10} color="blue.500">
              <IoCheckmark />
            </Icon>
          ) : (
            <Image src={getIconPathFromBadgeId(`quest_${toWords(quest.wave)}_badge`)} />
          )}
          <VStack gap={questIsComplete ? 1 : 3}>
            <Card.Title fontSize="2xl" fontFamily="heading" fontWeight="bold" textAlign="center">
              {questIsComplete ? 'Quest Completed!' : 'A Reward Awaits You'}
            </Card.Title>
            <Card.Description fontSize="md" fontWeight="500" textAlign="center">
              {questIsComplete
                ? `You earned a badge and ${quest.xpReward} Plume Points`
                : `Complete this quest to unlock a badge and ${quest.xpReward} Plume Points`}
            </Card.Description>
          </VStack>
          {questIsComplete && (
            <HStack flexWrap="wrap" gap={3}>
              <RoundedBadge bgColor="teal.800" color="fg.inverted" py={2}>
                <Image src="/assets/pp-circle.svg" boxSize={7} />
                You've earned {quest.xpReward} Plume Points
              </RoundedBadge>
              <RoundedBadge bgColor="brandBlue.800" color="fg.inverted" py={2}>
                <Image src={getIconPathFromBadgeId(`quest_${toWords(quest.wave)}_badge`, true)} boxSize={7} />
                New Badge Unlocked
              </RoundedBadge>
            </HStack>
          )}
          {questIsComplete && (
            <Text fontWeight="500" lineHeight="24px" color="text.secondary">
              Plume Points will be added to your account at 0:00 UTC
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};
